import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AppShell } from "@/components/AppShell";
import { useAuth } from "@/hooks/useAuth";
import { isAdmin as fetchIsAdmin } from "@/utils/roles.functions";
import {
  getAlertSettings,
  updateAlertSettings,
  type AlertNotificationSettings,
} from "@/utils/alert-settings.functions";
import { BellRing, ShieldAlert, Plus, X, Save, Mail } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin-alert-settings")({
  head: () => ({
    meta: [
      { title: "Alert notifications — LendyTrack" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AdminAlertSettings,
});

const ALL_SEVERITIES = ["low", "medium", "high", "critical"] as const;
type Severity = (typeof ALL_SEVERITIES)[number];

const sevTone = (s: Severity) =>
  s === "critical"
    ? "border-danger/60 bg-danger/15 text-danger"
    : s === "high"
      ? "border-danger/40 bg-danger/10 text-danger"
      : s === "medium"
        ? "border-accent/40 bg-accent/10 text-accent"
        : "border-border bg-muted text-muted-foreground";

function AdminAlertSettings() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [access, setAccess] = useState<"checking" | "allowed" | "denied">("checking");

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate({ to: "/auth" });
      return;
    }
    fetchIsAdmin()
      .then((r) => setAccess(r.isAdmin ? "allowed" : "denied"))
      .catch(() => setAccess("denied"));
  }, [user, authLoading, navigate]);

  const q = useQuery({
    queryKey: ["alert_settings"],
    enabled: access === "allowed",
    queryFn: async () => (await getAlertSettings()) as AlertNotificationSettings,
  });

  const [enabled, setEnabled] = useState(false);
  const [severities, setSeverities] = useState<Severity[]>(["high", "critical"]);
  const [recipients, setRecipients] = useState<string[]>([]);
  const [newEmail, setNewEmail] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (q.data) {
      setEnabled(q.data.enabled);
      setSeverities((q.data.severities as Severity[]) ?? []);
      setRecipients(q.data.recipients ?? []);
    }
  }, [q.data]);

  const toggleSev = (s: Severity) =>
    setSeverities((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));

  const addRecipient = () => {
    const v = newEmail.trim().toLowerCase();
    if (!v) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
      toast.error("Enter a valid email address");
      return;
    }
    if (recipients.includes(v)) {
      toast.error("Already in the list");
      return;
    }
    if (recipients.length >= 25) {
      toast.error("Maximum 25 recipients");
      return;
    }
    setRecipients((r) => [...r, v]);
    setNewEmail("");
  };

  const removeRecipient = (e: string) =>
    setRecipients((r) => r.filter((x) => x !== e));

  const dirty = useMemo(() => {
    if (!q.data) return false;
    const a = JSON.stringify({ enabled, severities: [...severities].sort(), recipients: [...recipients].sort() });
    const b = JSON.stringify({
      enabled: q.data.enabled,
      severities: [...(q.data.severities ?? [])].sort(),
      recipients: [...(q.data.recipients ?? [])].sort(),
    });
    return a !== b;
  }, [enabled, severities, recipients, q.data]);

  const save = async () => {
    if (enabled && recipients.length === 0) {
      toast.error("Add at least one recipient to enable notifications");
      return;
    }
    if (enabled && severities.length === 0) {
      toast.error("Select at least one severity to notify on");
      return;
    }
    setSaving(true);
    try {
      await updateAlertSettings({ data: { enabled, severities, recipients } });
      toast.success("Alert settings saved");
      qc.invalidateQueries({ queryKey: ["alert_settings"] });
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (access !== "allowed") {
    return (
      <AppShell>
        <div className="mx-auto mt-20 max-w-md rounded-2xl border border-border bg-card p-10 text-center shadow-soft">
          <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-danger/10 text-danger">
            <ShieldAlert className="h-6 w-6" />
          </div>
          <h1 className="mt-4 font-display text-xl font-semibold">
            {access === "checking" ? "Verifying access…" : "Admins only"}
          </h1>
          {access === "denied" && (
            <Link
              to="/dashboard"
              className="mt-6 inline-block rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"
            >
              Back to dashboard
            </Link>
          )}
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="mb-6 flex items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Admin</div>
          <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight">
            Alert notifications
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Choose which security alert severities should trigger email notifications and who receives them.
            Settings take effect once your sender email domain is verified.
          </p>
        </div>
        <Link
          to="/admin-audit"
          className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm font-semibold hover:bg-muted"
        >
          View audit log
        </Link>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <section className="lg:col-span-2 rounded-2xl border border-border bg-card p-6 shadow-soft">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="grid h-10 w-10 place-items-center rounded-full bg-primary/10 text-primary">
                <BellRing className="h-5 w-5" />
              </div>
              <div>
                <h2 className="font-display text-lg font-semibold">Email notifications</h2>
                <p className="mt-0.5 text-sm text-muted-foreground">
                  Master switch for outbound alert emails.
                </p>
              </div>
            </div>
            <label className="relative inline-flex cursor-pointer items-center">
              <input
                type="checkbox"
                className="peer sr-only"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
              />
              <span className="h-6 w-11 rounded-full bg-muted transition peer-checked:bg-primary" />
              <span className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-card shadow transition peer-checked:translate-x-5" />
            </label>
          </div>

          <div className="mt-8">
            <h3 className="font-display text-sm font-semibold uppercase tracking-widest text-muted-foreground">
              Severities to notify
            </h3>
            <div className="mt-3 flex flex-wrap gap-2">
              {ALL_SEVERITIES.map((s) => {
                const active = severities.includes(s);
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => toggleSev(s)}
                    className={`rounded-full border px-3 py-1.5 text-xs font-semibold uppercase tracking-widest transition ${
                      active ? sevTone(s) : "border-border bg-card text-muted-foreground hover:bg-muted"
                    }`}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Account lockouts always raise a high-severity alert; suspicious-access bursts escalate to medium → high → critical based on volume.
            </p>
          </div>

          <div className="mt-8">
            <h3 className="font-display text-sm font-semibold uppercase tracking-widest text-muted-foreground">
              Recipients ({recipients.length}/25)
            </h3>
            <div className="mt-3 flex gap-2">
              <div className="relative flex-1">
                <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addRecipient();
                    }
                  }}
                  placeholder="security@yourcompany.com"
                  className="w-full rounded-md border border-border bg-card py-2 pl-9 pr-3 text-sm outline-none focus:border-primary"
                />
              </div>
              <button
                type="button"
                onClick={addRecipient}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90"
              >
                <Plus className="h-4 w-4" /> Add
              </button>
            </div>

            {recipients.length === 0 ? (
              <p className="mt-3 text-sm text-muted-foreground">
                No recipients yet. Add at least one admin email to receive alerts.
              </p>
            ) : (
              <ul className="mt-3 flex flex-wrap gap-2">
                {recipients.map((email) => (
                  <li
                    key={email}
                    className="inline-flex items-center gap-2 rounded-full border border-border bg-muted/50 py-1 pl-3 pr-1 text-sm"
                  >
                    {email}
                    <button
                      type="button"
                      onClick={() => removeRecipient(email)}
                      aria-label={`Remove ${email}`}
                      className="grid h-6 w-6 place-items-center rounded-full text-muted-foreground hover:bg-danger/10 hover:text-danger"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="mt-8 flex items-center justify-end gap-2 border-t border-border pt-5">
            {q.data?.updated_at && (
              <span className="mr-auto text-xs text-muted-foreground">
                Last updated {new Date(q.data.updated_at).toLocaleString()}
              </span>
            )}
            <button
              onClick={save}
              disabled={!dirty || saving}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </section>

        <aside className="rounded-2xl border border-border bg-card p-6 shadow-soft">
          <h2 className="font-display text-base font-semibold">How alerts work</h2>
          <ul className="mt-3 space-y-3 text-sm text-muted-foreground">
            <li>
              <span className="font-semibold text-foreground">Detection.</span> A database trigger watches the audit log and raises an alert when 5+ suspicious or forbidden events occur within 15 minutes from the same user or IP.
            </li>
            <li>
              <span className="font-semibold text-foreground">Severity ladder.</span> 5–9 events → medium · 10–19 → high · 20+ → critical. Account lockouts are always high.
            </li>
            <li>
              <span className="font-semibold text-foreground">Delivery.</span> Pending alerts matching the selected severities will be emailed to your recipient list once an email sender domain is configured.
            </li>
            <li>
              <span className="font-semibold text-foreground">Retention.</span> Audit events are pruned after 90 days; alerts remain until acknowledged.
            </li>
          </ul>
        </aside>
      </div>
    </AppShell>
  );
}
